import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format as formatJalali } from "date-fns-jalali";

/** Standard `cn()` from shadcn/ui: merges Tailwind classes correctly. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * No-op kept for backward compatibility. We previously transliterated ASCII
 * digits to Persian Unicode (U+06F0..U+06F9) for visual style — but operators
 * read Latin numerals faster in dashboards and the project standard is now
 * "Latin digits everywhere, Persian labels". Existing call sites continue to
 * compile; output is identical to the input.
 */
export function toFa(input: string | number): string {
  return String(input);
}

/**
 * Latin digits + Latin thousands grouping (1,234,567). We use `en-US` rather
 * than the browser default to make snapshot tests deterministic and to avoid
 * the Arabic-Indic surprise on some browsers. The decoration is "Persian
 * label + Latin number" — which renders correctly under RTL.
 */
const NUM_FORMAT = new Intl.NumberFormat("en-US");
function fmtNumber(v: number, opts?: Intl.NumberFormatOptions): string {
  if (!Number.isFinite(v)) return "—";
  return opts
    ? new Intl.NumberFormat("en-US", opts).format(v)
    : NUM_FORMAT.format(v);
}

/** Format bytes with Persian unit labels but Latin digits. */
export function formatBytes(bytes: number | bigint | string | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  const n =
    typeof bytes === "bigint"
      ? Number(bytes)
      : typeof bytes === "string"
        ? Number(bytes)
        : bytes;
  if (!Number.isFinite(n) || n <= 0) return "0";
  const units = ["بایت", "کیلوبایت", "مگابایت", "گیگابایت", "ترابایت", "پتابایت"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const decimals = v >= 100 || i === 0 ? 0 : v >= 10 ? 1 : 2;
  return `${fmtNumber(v, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  })} ${units[i]}`;
}

/** Format an absolute date as a Persian relative phrase with Latin digits.
 * Rejects Invalid Date / pre-2020 sentinels (epoch zero) so a corrupt DB row
 * doesn't render as "57 سال پیش" instead of "بدون بکاپ". */
export function timeAgo(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime()) || d.getUTCFullYear() < 2020) return "—";
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 0) return "هم‌اکنون";
  if (diffSec < 60) return `${diffSec} ثانیه پیش`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m} دقیقه پیش`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ساعت پیش`;
  const d2 = Math.floor(h / 24);
  if (d2 < 30) return `${d2} روز پیش`;
  const mo = Math.floor(d2 / 30);
  if (mo < 12) return `${mo} ماه پیش`;
  return `${Math.floor(mo / 12)} سال پیش`;
}

/**
 * Format a duration (milliseconds) as a short Persian string with two
 * significant units, e.g. `2 دقیقه و 15 ثانیه`, `1 ساعت و 22 دقیقه`,
 * `3 روز و 4 ساعت`. Drops the second unit when it's zero. Used for
 * "elapsed since started" on a running job and "total runtime" on a
 * finished job — the operator scans this column to spot stuck jobs.
 *
 * Negative / undefined inputs collapse to `—`. We intentionally don't
 * fall back to "0 seconds" because that's almost always a missing-data
 * bug we want to see, not a real zero-length job.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return "—";
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec} ثانیه`;
  const totalMin = Math.floor(totalSec / 60);
  const secRem = totalSec - totalMin * 60;
  if (totalMin < 60) {
    return secRem > 0 ? `${totalMin} دقیقه و ${secRem} ثانیه` : `${totalMin} دقیقه`;
  }
  const totalHr = Math.floor(totalMin / 60);
  const minRem = totalMin - totalHr * 60;
  if (totalHr < 24) {
    return minRem > 0 ? `${totalHr} ساعت و ${minRem} دقیقه` : `${totalHr} ساعت`;
  }
  const days = Math.floor(totalHr / 24);
  const hrRem = totalHr - days * 24;
  return hrRem > 0 ? `${days} روز و ${hrRem} ساعت` : `${days} روز`;
}

/**
 * Compute elapsed/total duration for a job-like object that may still
 * be running. If `endTime` is null, we use `Date.now()` (i.e. the job is
 * still ticking). Returns null if `startTime` is missing — caller should
 * render `—` in that case.
 *
 * `nowMs` lets callers pass a shared "now" so a list of in-progress jobs
 * doesn't drift across rows; defaults to Date.now().
 */
export function jobDurationMs(
  startTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!startTime) return null;
  const start = typeof startTime === "string" ? new Date(startTime) : startTime;
  if (!(start instanceof Date) || isNaN(start.getTime())) return null;
  const end = endTime
    ? (typeof endTime === "string" ? new Date(endTime) : endTime)
    : null;
  const endMs = end && !isNaN(end.getTime()) ? end.getTime() : nowMs;
  return Math.max(0, endMs - start.getTime());
}

/**
 * Format an absolute timestamp as Jalali (Solar Hijri) date + time with
 * Latin digits, e.g. `1404/02/28 - 14:32`. Used for snapshot timestamps so
 * the customer can quote them back to us when confirming a restore
 * (relative phrasing like "5 ساعت پیش" would have drifted by the time the
 * customer responds via ticket).
 */
export function jalaliDateTime(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = typeof input === "string" ? new Date(input) : input;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "—";
  return formatJalali(d, "yyyy/MM/dd - HH:mm");
}

/**
 * Compact single-line Jalali "date + time" string for table cells.
 *
 *   today  → `HH:mm`                  (5 chars,  e.g. `14:30`)
 *   < 365d → `MM/dd - HH:mm`          (13 chars, e.g. `02/28 - 14:30`)
 *   older  → `yyyy/MM/dd - HH:mm`     (18 chars, e.g. `1404/02/28 - 14:30`)
 *
 * The ` - ` separator gives a clear visual break between date and time
 * within the cell — without it the two halves bleed together
 * (`1404/02/28 14:30` looks like one numeric blob at small sizes).
 *
 * Output is pre-trimmed to never wrap so callers can drop the
 * `whitespace-nowrap` repeated everywhere. Pair with `lat-num tabular`
 * for column-aligned digits when used inside a table.
 *
 * Returns `—` for null/invalid/epoch-zero inputs (same guard rails as
 * `timeAgo`) so rows that have never had the field populated render a
 * neutral dash instead of a misleading `1349/10/11 03:30` from a
 * corrupt timestamp.
 */
export function jalaliDateTimeCompact(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = typeof input === "string" ? new Date(input) : input;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "—";
  if (d.getUTCFullYear() < 2020) return "—";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return formatJalali(d, "HH:mm");
  const diffMs = now.getTime() - d.getTime();
  const within1Year = diffMs >= 0 && diffMs < 365 * 24 * 60 * 60 * 1000;
  return within1Year
    ? formatJalali(d, "MM/dd - HH:mm")
    : formatJalali(d, "yyyy/MM/dd - HH:mm");
}

/**
 * Compact Jalali date only (no time) — for table rows where a full
 * timestamp would clutter the row. Format `1404/02/28`.
 */
export function jalaliDate(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = typeof input === "string" ? new Date(input) : input;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "—";
  return formatJalali(d, "yyyy/MM/dd");
}

/** Format an integer with Latin thousands grouping. */
export function num(n: number | bigint | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return fmtNumber(Number(n));
}
